import type { Report, TacticalPlay, TradingCall, UnusualActivityItem, FullListItem } from '../types/report';
import { normalizeElitePulseReportFromApi } from './elitePulseReportApiNormalize';
import {
  formatConfidenceAr,
  formatConfidenceEn,
  parseTradingCallConfidencePct,
} from './elitePulseConfidence';

export type ElitePulseOpportunityPreview = {
  symbol: string;
  headline: string;
  /** Secondary line: strikes, entry/target/stop, DTE (Arabic in report alerts). */
  detail?: string;
  kind: 'tactical' | 'trading_call' | 'unusual' | 'focus' | 'strategic' | 'summary';
};

export type ElitePulseReportNotifyPayload = {
  fingerprint: string;
  generatedAt: string;
  title: string;
  previews: ElitePulseOpportunityPreview[];
  previewText: string;
};

const MAX_PREVIEWS = 5;

function normSym(s: unknown): string {
  return String(s ?? '')
    .trim()
    .toUpperCase();
}

function pushPreview(
  out: ElitePulseOpportunityPreview[],
  seen: Set<string>,
  item: ElitePulseOpportunityPreview,
): void {
  if (out.length >= MAX_PREVIEWS) return;
  const key = `${item.kind}:${item.symbol}:${item.headline.slice(0, 40)}`;
  if (seen.has(key)) return;
  seen.add(key);
  out.push(item);
}

function tacticalHeadline(p: TacticalPlay): string {
  const strat = (p.strategy || '').trim();
  const rr = (p.rr || '').trim();
  if (strat && rr) return `${strat} · R:R ${rr}`;
  return strat || 'Tactical opportunity';
}

function tacticalHeadlineAr(p: TacticalPlay): string {
  const strat = (p.strategy || '').trim();
  const rr = (p.rr || '').trim();
  const dte = (p.dte || '').trim();
  const bits = [strat, rr ? `نسبة مخاطرة/عائد ${rr}` : '', dte ? `أجل ${dte}` : ''].filter(Boolean);
  return bits.join(' · ') || 'فرصة تكتيكية';
}

function fmtUsd(n: number): string {
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function tradingCallHeadline(c: TradingCall): string {
  const parts = [(c.strategy || '').trim(), (c.direction || '').trim()].filter(Boolean);
  const pct = parseTradingCallConfidencePct(c);
  if (pct != null) parts.push(formatConfidenceEn(pct));
  else if (c.confidence) parts.push(String(c.confidence));
  return parts.join(' · ') || 'Trading call';
}

function tradingCallHeadlineAr(c: TradingCall): string {
  const strat = (c.strategy || '').trim();
  const dir = (c.direction || '').trim().toLowerCase();
  const dirAr =
    /bull|long|call/i.test(dir) ? 'صاعد'
    : /bear|short|put/i.test(dir) ? 'هابط'
    : dir ? dir : '';
  const confAr =
    (c.confidence_label_ar || '').trim() || formatConfidenceAr(parseTradingCallConfidencePct(c));
  const bits = [strat, dirAr, confAr].filter(Boolean);
  return bits.join(' · ') || 'توصية تداول';
}

function tradingCallDetail(c: TradingCall): string | undefined {
  const parts: string[] = [];
  if (c.strikes) parts.push(c.strikes);
  if (c.dte) parts.push(`DTE ${c.dte}`);
  if (c.entry != null) {
    parts.push(`Entry ${fmtUsd(c.entry)}`);
    if (c.target != null) parts.push(`Target ${fmtUsd(c.target)}`);
    if (c.stop != null) parts.push(`Stop ${fmtUsd(c.stop)}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
}

function tradingCallDetailAr(c: TradingCall): string | undefined {
  const parts: string[] = [];
  if (c.strikes) parts.push(c.strikes);
  if (c.dte) parts.push(`أجل ${c.dte}`);
  if (c.entry != null) {
    parts.push(`دخول ${fmtUsd(c.entry)}`);
    if (c.target != null) parts.push(`هدف ${fmtUsd(c.target)}`);
    if (c.stop != null) parts.push(`وقف ${fmtUsd(c.stop)}`);
  }
  if (c.rationale) parts.push(c.rationale.slice(0, 80));
  return parts.length ? parts.join(' · ') : undefined;
}

function unusualHeadline(u: UnusualActivityItem): string {
  const sig = (u.signal || u.direction || '').trim();
  const prem =
    typeof u.premium === 'number' && u.premium > 0
      ? `$${(u.premium / 1_000_000).toFixed(1)}M`
      : '';
  return [sig, prem].filter(Boolean).join(' · ') || 'Unusual flow';
}

function unusualHeadlineAr(u: UnusualActivityItem): string {
  const sig = (u.signal || u.direction || '').trim();
  const prem =
    typeof u.premium === 'number' && u.premium > 0
      ? `علاوة ${(u.premium / 1_000_000).toFixed(1)} مليون $`
      : '';
  return [sig || 'تدفق غير عادي', prem].filter(Boolean).join(' · ');
}

function focusHeadline(f: FullListItem): string {
  const tags = (f.tags || []).slice(0, 2).join(', ');
  const impact = (f.impact || '').trim();
  if (tags && impact) return `${tags} · ${impact}`;
  return tags || impact || 'Focus symbol';
}

function focusHeadlineAr(f: FullListItem): string {
  const tags = (f.tags || []).slice(0, 2).join('، ');
  const impact = (f.impact || '').trim();
  if (tags && impact) return `${tags} · ${impact}`;
  return tags || impact || 'رمز تحت المراقبة';
}

function headlineForLanguage<T>(
  item: T,
  en: (x: T) => string,
  ar: (x: T) => string,
  language: string,
): string {
  return language === 'ar' ? ar(item) : en(item);
}

/** Stable id for a pulse run — generator time + content, not API reload clock. */
export function elitePulseReportFingerprint(
  report: Report | null,
  summaryJson?: Record<string, unknown> | null,
): string {
  const sj = summaryJson && typeof summaryJson === 'object' ? summaryJson : null;
  const parts: string[] = [];

  const fromSummary =
    sj &&
    String(sj.report_generated_utc ?? sj.generated_utc ?? sj.data_as_of_utc ?? sj.last_sync_utc ?? '').trim();
  const meta = report?.meta;
  const fromMeta = String(meta?.summaryAsOf ?? meta?.generatedAt ?? '').trim();
  const ts = fromSummary || fromMeta;
  if (ts) parts.push(`ts:${ts}`);

  const cm = sj?.critical_mass;
  if (Array.isArray(cm) && cm.length) {
    const syms = cm
      .slice(0, 20)
      .map((row) =>
        typeof row === 'object' && row != null
          ? String((row as Record<string, unknown>).symbol ?? (row as Record<string, unknown>).underlying ?? '')
          : String(row ?? ''),
      )
      .filter(Boolean)
      .sort()
      .join(',');
    if (syms) parts.push(`cm:${syms.slice(0, 120)}`);
  }

  const tc = report?.tradingCalls;
  if (Array.isArray(tc) && tc.length) {
    const sig = tc
      .slice(0, 12)
      .map((c) => [c.symbol, c.strategy, c.direction, c.confidence, c.target, c.stop].join('|'))
      .join('\n');
    if (sig) parts.push(`tc:${sig.slice(0, 160)}`);
  }

  return parts.join('|');
}

export function extractElitePulseOpportunityPreviews(
  report: Report | null,
  language = 'ar',
): ElitePulseOpportunityPreview[] {
  if (!report) return [];
  const out: ElitePulseOpportunityPreview[] = [];
  const seen = new Set<string>();

  for (const p of report.tactical || []) {
    const sym = normSym(p.symbol);
    if (!sym) continue;
    pushPreview(out, seen, {
      symbol: sym,
      headline: headlineForLanguage(p, tacticalHeadline, tacticalHeadlineAr, language),
      kind: 'tactical',
    });
  }
  for (const c of report.tradingCalls || []) {
    const sym = normSym(c.symbol);
    if (!sym) continue;
    pushPreview(out, seen, {
      symbol: sym,
      headline: headlineForLanguage(c, tradingCallHeadline, tradingCallHeadlineAr, language),
      detail: headlineForLanguage(c, tradingCallDetail, tradingCallDetailAr, language),
      kind: 'trading_call',
    });
  }
  const unusual = [...(report.unusualActivity || [])].sort((a, b) => (b.premium ?? 0) - (a.premium ?? 0));
  for (const u of unusual) {
    const sym = normSym(u.symbol);
    if (!sym) continue;
    pushPreview(out, seen, {
      symbol: sym,
      headline: headlineForLanguage(u, unusualHeadline, unusualHeadlineAr, language),
      kind: 'unusual',
    });
  }
  for (const f of report.fullList || []) {
    const sym = normSym(f.symbol);
    if (!sym) continue;
    pushPreview(out, seen, {
      symbol: sym,
      headline: headlineForLanguage(f, focusHeadline, focusHeadlineAr, language),
      kind: 'focus',
    });
  }
  for (const p of report.strategic || []) {
    const sym = normSym(p.symbol);
    if (!sym) continue;
    const strat = (p.strategy || '').trim();
    const headline =
      language === 'ar'
        ? (strat ? `${strat} (${p.tenor || '—'})` : 'صفقة استراتيجية')
        : (strat ? `${strat} (${p.tenor || '—'})` : 'Strategic play');
    pushPreview(out, seen, { symbol: sym, headline, kind: 'strategic' });
  }

  const qp = (report.quickPlaysSummary || '').trim();
  if (out.length < MAX_PREVIEWS && qp && !/no.*(high.conviction|plays|data)|monitor for/i.test(qp)) {
    const line = qp.split(/\n/).map((l) => l.trim()).find(Boolean) || qp;
    pushPreview(out, seen, {
      symbol: 'PULSE',
      headline: line.slice(0, 120),
      kind: 'summary',
    });
  }

  return out;
}

export function formatElitePulsePreviewText(
  previews: ElitePulseOpportunityPreview[],
  language: string,
): string {
  if (!previews.length) {
    return language === 'ar'
      ? 'تقرير نبض النخبة الجديد متاح — افتح التقرير للتفاصيل.'
      : 'New Elite Pulse report is available — open the report for details.';
  }
  return previews
    .map((p) => {
      const base = p.symbol === 'PULSE' ? p.headline : `${p.symbol}: ${p.headline}`;
      return p.detail ? `${base}\n  ${p.detail}` : base;
    })
    .join('\n');
}

export function buildElitePulseReportNotifyPayload(
  rawReport: unknown,
  summaryJson?: Record<string, unknown> | null,
  language = 'ar',
): ElitePulseReportNotifyPayload | null {
  const report = normalizeElitePulseReportFromApi(rawReport);
  const fingerprint = elitePulseReportFingerprint(report, summaryJson);
  if (!fingerprint) return null;

  const sj = summaryJson && typeof summaryJson === 'object' ? summaryJson : null;
  const generatedAt =
    (sj && String(sj.report_generated_utc ?? sj.generated_utc ?? sj.data_as_of_utc ?? '').trim()) ||
    report?.meta?.summaryAsOf ||
    report?.meta?.generatedAt ||
    new Date().toISOString();
  const title =
    (report?.meta?.title || '').trim() ||
    (language === 'ar' ? 'تقرير نبض النخبة' : 'Elite Pulse report');
  const previews = extractElitePulseOpportunityPreviews(report, language);
  const previewText = formatElitePulsePreviewText(previews, language);

  return { fingerprint, generatedAt, title, previews, previewText };
}

export function requestBrowserNotificationPermission(): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'default') {
    void Notification.requestPermission();
  }
}

export function sendElitePulseBrowserNotification(
  payload: ElitePulseReportNotifyPayload,
  language: string,
): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;

  const title =
    language === 'ar' ? 'تقرير نبض النخبة الجديد' : 'New Elite Pulse report';
  const body = payload.previewText.slice(0, 240);

  const fire = () => {
    try {
      const n = new Notification(title, {
        body,
        icon: '/logo.png',
        tag: `elite-pulse-${payload.fingerprint}`,
      });
      n.onclick = () => {
        window.focus();
        window.dispatchEvent(
          new CustomEvent('mmec-elite-pulse-new-report', {
            detail: { ...payload, openChat: true, navigate: true },
          }),
        );
        n.close();
      };
    } catch {
      /* ignore */
    }
  };

  if (Notification.permission === 'granted') {
    fire();
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((p) => {
      if (p === 'granted') fire();
    });
  }
}

export const ELITE_PULSE_NEW_REPORT_EVENT = 'mmec-elite-pulse-new-report';
